import React, { useState, useEffect } from 'react';

interface LayerThemeEditorProps {
  layer: any;
  isOpen: boolean;
  onClose: () => void;
  onSave: (layerId: number, styleConfig: any) => Promise<void>;
}

interface StyleConfig {
  fillColor: string;
  fillOpacity: number;
  strokeColor: string;
  strokeWidth: number;
  strokeOpacity: number;
  pointRadius: number;
  pointIcon?: string;
  labelField?: string;
  labelColor: string;
  labelSize: number;
}

const LayerThemeEditor: React.FC<LayerThemeEditorProps> = ({ layer, isOpen, onClose, onSave }) => {
  const [styleConfig, setStyleConfig] = useState<StyleConfig>({
    fillColor: '#3b82f6',
    fillOpacity: 0.5,
    strokeColor: '#1e40af',
    strokeWidth: 2,
    strokeOpacity: 1,
    pointRadius: 8,
    pointIcon: '',
    labelField: '',
    labelColor: '#000000',
    labelSize: 12
  });
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'fill' | 'stroke' | 'point' | 'label'>('fill');

  useEffect(() => {
    if (layer && layer.default_style) {
      const existingStyle = typeof layer.default_style === 'string'
        ? JSON.parse(layer.default_style)
        : layer.default_style;

      setStyleConfig({
        fillColor: existingStyle.fillColor || '#3b82f6',
        fillOpacity: existingStyle.fillOpacity || 0.5,
        strokeColor: existingStyle.strokeColor || '#1e40af',
        strokeWidth: existingStyle.strokeWidth || 2,
        strokeOpacity: existingStyle.strokeOpacity || 1,
        pointRadius: existingStyle.pointRadius || 8,
        pointIcon: existingStyle.pointIcon || '',
        labelField: existingStyle.labelField || '',
        labelColor: existingStyle.labelColor || '#000000',
        labelSize: existingStyle.labelSize || 12
      });
    }
  }, [layer]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await onSave(layer.id, styleConfig);
      onClose();
    } catch (error) {
      console.error('Error saving theme:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const isPolygon = layer?.geometry_type?.includes('Polygon');
  const isLineString = layer?.geometry_type?.includes('LineString');
  const isPoint = layer?.geometry_type?.includes('Point');

  return (
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg">
              <i className="ri-palette-line text-xl text-white"></i>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Theme Editor
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {layer?.layer_name} ({layer?.geometry_type})
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 sticky top-[73px] z-10">
          {isPolygon && (
            <button
              onClick={() => setActiveTab('fill')}
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'fill'
                  ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400 bg-white dark:bg-gray-800'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <i className="ri-paint-fill text-lg mr-2"></i>
              Fill
            </button>
          )}
          {(isPolygon || isLineString) && (
            <button
              onClick={() => setActiveTab('stroke')}
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'stroke'
                  ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400 bg-white dark:bg-gray-800'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <i className="ri-edit-line text-lg mr-2"></i>
              Stroke
            </button>
          )}
          {isPoint && (
            <button
              onClick={() => setActiveTab('point')}
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'point'
                  ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400 bg-white dark:bg-gray-800'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <i className="ri-map-pin-line text-lg mr-2"></i>
              Point
            </button>
          )}
          <button
            onClick={() => setActiveTab('label')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'label'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400 bg-white dark:bg-gray-800'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <i className="ri-text text-lg mr-2"></i>
            Labels
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Fill Tab */}
          {activeTab === 'fill' && isPolygon && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fill Color
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={styleConfig.fillColor}
                    onChange={(e) => setStyleConfig({ ...styleConfig, fillColor: e.target.value })}
                    className="w-20 h-10 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={styleConfig.fillColor}
                    onChange={(e) => setStyleConfig({ ...styleConfig, fillColor: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm"
                    placeholder="#3b82f6"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fill Opacity: {(styleConfig.fillOpacity * 100).toFixed(0)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={styleConfig.fillOpacity}
                  onChange={(e) => setStyleConfig({ ...styleConfig, fillOpacity: parseFloat(e.target.value) })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                />
              </div>

              {/* Preview */}
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Preview</p>
                <div className="flex items-center justify-center p-8">
                  <svg width="150" height="100">
                    <rect
                      x="10"
                      y="10"
                      width="130"
                      height="80"
                      fill={styleConfig.fillColor}
                      fillOpacity={styleConfig.fillOpacity}
                      stroke={styleConfig.strokeColor}
                      strokeWidth={styleConfig.strokeWidth}
                      strokeOpacity={styleConfig.strokeOpacity}
                    />
                  </svg>
                </div>
              </div>
            </div>
          )}

          {/* Stroke Tab */}
          {activeTab === 'stroke' && (isPolygon || isLineString) && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Stroke Color
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={styleConfig.strokeColor}
                    onChange={(e) => setStyleConfig({ ...styleConfig, strokeColor: e.target.value })}
                    className="w-20 h-10 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={styleConfig.strokeColor}
                    onChange={(e) => setStyleConfig({ ...styleConfig, strokeColor: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm"
                    placeholder="#1e40af"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Stroke Width: {styleConfig.strokeWidth}px
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  step="1"
                  value={styleConfig.strokeWidth}
                  onChange={(e) => setStyleConfig({ ...styleConfig, strokeWidth: parseInt(e.target.value) })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Stroke Opacity: {(styleConfig.strokeOpacity * 100).toFixed(0)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={styleConfig.strokeOpacity}
                  onChange={(e) => setStyleConfig({ ...styleConfig, strokeOpacity: parseFloat(e.target.value) })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                />
              </div>

              {/* Preview */}
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Preview</p>
                <div className="flex items-center justify-center p-8">
                  {isLineString ? (
                    <svg width="200" height="100">
                      <polyline
                        points="20,80 60,30 100,60 140,20 180,70"
                        fill="none"
                        stroke={styleConfig.strokeColor}
                        strokeWidth={styleConfig.strokeWidth}
                        strokeOpacity={styleConfig.strokeOpacity}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg width="150" height="100">
                      <rect
                        x="10"
                        y="10"
                        width="130"
                        height="80"
                        fill={styleConfig.fillColor}
                        fillOpacity={styleConfig.fillOpacity}
                        stroke={styleConfig.strokeColor}
                        strokeWidth={styleConfig.strokeWidth}
                        strokeOpacity={styleConfig.strokeOpacity}
                      />
                    </svg>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Point Tab */}
          {activeTab === 'point' && isPoint && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Point Color
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={styleConfig.fillColor}
                    onChange={(e) => setStyleConfig({ ...styleConfig, fillColor: e.target.value })}
                    className="w-20 h-10 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={styleConfig.fillColor}
                    onChange={(e) => setStyleConfig({ ...styleConfig, fillColor: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm"
                    placeholder="#3b82f6"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Point Radius: {styleConfig.pointRadius}px
                </label>
                <input
                  type="range"
                  min="4"
                  max="20"
                  step="1"
                  value={styleConfig.pointRadius}
                  onChange={(e) => setStyleConfig({ ...styleConfig, pointRadius: parseInt(e.target.value) })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Border Color
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={styleConfig.strokeColor}
                    onChange={(e) => setStyleConfig({ ...styleConfig, strokeColor: e.target.value })}
                    className="w-20 h-10 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={styleConfig.strokeColor}
                    onChange={(e) => setStyleConfig({ ...styleConfig, strokeColor: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm"
                    placeholder="#1e40af"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Border Width: {styleConfig.strokeWidth}px
                </label>
                <input
                  type="range"
                  min="0"
                  max="5"
                  step="1"
                  value={styleConfig.strokeWidth}
                  onChange={(e) => setStyleConfig({ ...styleConfig, strokeWidth: parseInt(e.target.value) })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                />
              </div>

              {/* Preview */}
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Preview</p>
                <div className="flex items-center justify-center p-8">
                  <svg width="150" height="100">
                    <circle
                      cx="75"
                      cy="50"
                      r={styleConfig.pointRadius}
                      fill={styleConfig.fillColor}
                      stroke={styleConfig.strokeColor}
                      strokeWidth={styleConfig.strokeWidth}
                    />
                  </svg>
                </div>
              </div>
            </div>
          )}

          {/* Label Tab */}
          {activeTab === 'label' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Label Field
                </label>
                <input
                  type="text"
                  value={styleConfig.labelField || ''}
                  onChange={(e) => setStyleConfig({ ...styleConfig, labelField: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="name"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Column name to use for labels (leave empty for no labels)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Label Color
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={styleConfig.labelColor}
                    onChange={(e) => setStyleConfig({ ...styleConfig, labelColor: e.target.value })}
                    className="w-20 h-10 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={styleConfig.labelColor}
                    onChange={(e) => setStyleConfig({ ...styleConfig, labelColor: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm"
                    placeholder="#000000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Label Size: {styleConfig.labelSize}px
                </label>
                <input
                  type="range"
                  min="8"
                  max="24"
                  step="1"
                  value={styleConfig.labelSize}
                  onChange={(e) => setStyleConfig({ ...styleConfig, labelSize: parseInt(e.target.value) })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 sticky bottom-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="ri-save-line"></i>
                Save Theme
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LayerThemeEditor;
